import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TYPE_META = {
  vocabulary:       { label: 'Từ vựng',         color: '#5AC0B5', bg: '#E6F6F4' },
  grammar:          { label: 'Ngữ pháp',        color: '#5E9E3E', bg: '#E8F3D9' },
  hint:             { label: 'Mẹo',             color: '#5B2E5E', bg: '#EFE3F0' },
  trap:             { label: 'Bẫy',             color: '#E76F51', bg: '#FCE4E8' },
  vocabulary_topic: { label: 'Chủ đề từ vựng',  color: '#3DC9B5', bg: '#E0F5F2' },
  grammar_topic:    { label: 'Chủ đề ngữ pháp', color: '#88C057', bg: '#E8F3D9' },
};

// Map annotation type aliases to tag_type_code used in tag_details.
const ANNOTATION_TYPE_MAP = {
  vocab: 'vocabulary',
  vocabulary: 'vocabulary',
  vocabulary_topic: 'vocabulary_topic',
  grammar: 'grammar',
  grammar_topic: 'grammar_topic',
  hint: 'hint',
  trap: 'trap',
};

// Parse text containing `[label]{TAG_CODE|tag_type}` annotations.
// Returns an array of { type: 'text', value } or { type: 'tag', label, code, tagType }.
const parseAnnotated = (text) => {
  if (!text) return [];
  const re = /\[([^\]]+)\]\{([^}|]+)\|([^}]+)\}/g;
  const out = [];
  let lastIdx = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) out.push({ type: 'text', value: text.slice(lastIdx, m.index) });
    out.push({
      type: 'tag',
      label: m[1],
      code: m[2].trim(),
      tagType: ANNOTATION_TYPE_MAP[m[3].trim().toLowerCase()] || m[3].trim(),
    });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push({ type: 'text', value: text.slice(lastIdx) });
  return out;
};

const tagFullName = (tag) => {
  if (!tag?.name) return '';
  if (typeof tag.name === 'string') return tag.name;
  if (typeof tag.name === 'object') return tag.name.en || tag.name.vi || '';
  return String(tag.name);
};

// Expand each `tag` segment whose tag.name is a longer phrase containing the
// AI-bracketed label. If neighboring text matches the prefix/suffix of tag.name,
// splice those characters into the tag's label so the whole phrase becomes one
// clickable unit (e.g. "a [lot]{VOC_LOT_01|vocab} of" → button label "a lot of").
const expandPhraseSegments = (segments, tagsByCode) => {
  if (!tagsByCode || segments.length === 0) return segments;
  const out = segments.map((s) => ({ ...s }));
  for (let i = 0; i < out.length; i++) {
    const seg = out[i];
    if (seg.type !== 'tag') continue;
    const tag = tagsByCode[seg.code];
    if (!tag) continue;
    const fullName = tagFullName(tag).trim();
    if (!fullName) continue;
    const labelLc = seg.label.toLowerCase();
    const fullLc = fullName.toLowerCase();
    if (fullLc === labelLc) continue;
    const idx = fullLc.indexOf(labelLc);
    if (idx < 0) continue;
    const prefix = fullName.slice(0, idx);
    const suffix = fullName.slice(idx + seg.label.length);
    const prevSeg = i > 0 ? out[i - 1] : null;
    const nextSeg = i + 1 < out.length ? out[i + 1] : null;

    let prefixOK = !prefix;
    let suffixOK = !suffix;
    let actualPrefix = '';
    let actualSuffix = '';
    if (prefix) {
      if (prevSeg && prevSeg.type === 'text' && prevSeg.value.length >= prefix.length) {
        const tail = prevSeg.value.slice(-prefix.length);
        if (tail.toLowerCase() === prefix.toLowerCase()) {
          prefixOK = true;
          actualPrefix = tail;
        }
      }
    }
    if (suffix) {
      if (nextSeg && nextSeg.type === 'text' && nextSeg.value.length >= suffix.length) {
        const head = nextSeg.value.slice(0, suffix.length);
        if (head.toLowerCase() === suffix.toLowerCase()) {
          suffixOK = true;
          actualSuffix = head;
        }
      }
    }
    if (!prefixOK || !suffixOK) continue;

    seg.label = `${actualPrefix}${seg.label}${actualSuffix}`;
    if (prefix && prevSeg) {
      prevSeg.value = prevSeg.value.slice(0, prevSeg.value.length - prefix.length);
    }
    if (suffix && nextSeg) {
      nextSeg.value = nextSeg.value.slice(suffix.length);
    }
  }
  return out.filter((s) => !(s.type === 'text' && s.value === ''));
};

const RenderAnnotated = ({ text, tagsByCode, onSelectTag }) => {
  const segments = expandPhraseSegments(parseAnnotated(text), tagsByCode);
  return (
    <span>
      {segments.map((seg, i) => {
        if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
        const meta = TYPE_META[seg.tagType] || TYPE_META.vocabulary;
        const tag = tagsByCode?.[seg.code];
        return (
          <button
            type="button"
            key={i}
            onClick={(e) => {
              e.stopPropagation();
              if (tag) onSelectTag(tag);
            }}
            disabled={!tag}
            title={tag ? `Xem chi tiết` : 'Tag không có trong câu này'}
            style={{
              display: 'inline',
              background: 'transparent',
              color: meta.color,
              border: 'none',
              padding: 0,
              margin: 0,
              fontSize: 'inherit',
              fontWeight: 800,
              cursor: tag ? 'pointer' : 'default',
              opacity: tag ? 1 : 0.6,
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
              textDecoration: tag ? 'underline dotted' : 'none',
              textUnderlineOffset: '2px',
              fontFamily: 'inherit',
            }}
          >
            {seg.label}
          </button>
        );
      })}
    </span>
  );
};

const pickLang = (val, prefer = 'vi') => {
  if (val == null) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') return val[prefer] || val.en || val.vi || null;
  return String(val);
};

const Section = ({ icon, title, children }) => (
  <div style={{ marginTop: 12 }}>
    <div style={{
      fontSize: 11, fontWeight: 800, color: '#6B4F2B', marginBottom: 4,
      letterSpacing: 0.4, textTransform: 'uppercase',
    }}>
      <span style={{ marginRight: 4 }}>{icon}</span>{title}
    </div>
    <div style={{ fontSize: 14, color: '#2D2D2D', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
      {children}
    </div>
  </div>
);

const Bilingual = ({ vi, en }) => (!vi && !en) ? null : (
  <>
    {vi && <div style={{ fontWeight: 700 }}>{vi}</div>}
    {en && <div style={{ color: '#6B6B6B', fontStyle: 'italic', marginTop: 2 }}>{en}</div>}
  </>
);

// Static vocab image base — same convention as LibraryPage:
// {MEDIA_BASE}/media/images/vocabulary/{tag.code}.webp
const MEDIA_BASE = process.env.REACT_APP_MEDIA_URL || 'https://api-staging.hupath.com/uploads';
const VOCAB_IMG_BASE = `${MEDIA_BASE}/media/images/vocabulary/`;

const VocabImage = ({ tagCode, size = 200 }) => {
  const [failed, setFailed] = React.useState(false);
  if (!tagCode || failed) return null;
  return (
    <div style={{
      flexShrink: 0,
      width: size, height: size,
      borderRadius: 16,
      overflow: 'hidden',
      background: '#FFF',
      border: '1px solid #E8E0CC',
      boxShadow: '0 2px 10px rgba(139,111,71,0.12)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <img
        src={`${VOCAB_IMG_BASE}${tagCode}.webp`}
        alt={tagCode}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          width: '100%', height: '100%', objectFit: 'contain', display: 'block', padding: 8,
        }}
      />
    </div>
  );
};

const VocabDetail = ({ tag }) => {
  const c = tag?.content || {};
  const wordEn = pickLang(c.name, 'en') || pickLang(c.name);
  const nameVi = pickLang(c.name, 'vi');
  return (
    <div>
      {/* Top row: word/pos/pronunciation/name_vi on the left, image on the right.
          flexWrap: nowrap so they always stay side-by-side; min-width: 0 lets
          the text column shrink instead of pushing the image down. */}
      <div style={{
        display: 'flex', flexDirection: 'row', gap: 14,
        alignItems: 'center', flexWrap: 'nowrap',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 26, fontWeight: 900, color: '#2D2D2D', lineHeight: 1.1 }}>{wordEn}</span>
            {c.pos && <span style={{ fontSize: 12, fontWeight: 700, color: '#6B6B6B' }}>({c.pos})</span>}
          </div>
          {c.pronunciation_uk && (
            <div style={{ fontSize: 14, color: '#9A9A9A', marginTop: 4, fontFamily: 'monospace' }}>
              {c.pronunciation_uk}
            </div>
          )}
          {nameVi && nameVi !== wordEn && (
            <div style={{ fontSize: 17, color: '#5B2E5E', fontWeight: 700, marginTop: 6 }}>
              {nameVi}
            </div>
          )}
        </div>
        <VocabImage tagCode={tag?.code} size={170} />
      </div>

      {/* Definition + example fill the full width below */}
      {(c.definition?.vi || c.definition?.en) && (
        <Section icon="📖" title="Định nghĩa">
          <Bilingual vi={c.definition?.vi} en={c.definition?.en} />
        </Section>
      )}
      {(c.example?.vi || c.example?.en) && (
        <Section icon="✏️" title="Ví dụ">
          <Bilingual vi={c.example?.vi} en={c.example?.en} />
        </Section>
      )}
    </div>
  );
};

const GrammarDetail = ({ c }) => (
  <>
    <div style={{ fontSize: 20, fontWeight: 900, color: '#2D2D2D' }}>
      {pickLang(c.name, 'en') || pickLang(c.name)}
    </div>
    {c.formula && (
      <div style={{
        marginTop: 8, display: 'inline-block', background: '#FFF',
        border: '1px solid #E8E0CC', borderRadius: 8, padding: '4px 10px',
        fontSize: 14, fontWeight: 700, color: '#2D2D2D', fontFamily: 'monospace',
      }}>{c.formula}</div>
    )}
    {(c.usage?.vi || c.usage?.en) && (
      <Section icon="🎯" title="Cách dùng">
        <Bilingual vi={c.usage?.vi} en={c.usage?.en} />
      </Section>
    )}
    {(c.example?.vi || c.example?.en) && (
      <Section icon="✏️" title="Ví dụ">
        <Bilingual vi={c.example?.vi} en={c.example?.en} />
      </Section>
    )}
  </>
);

const HintTrapDetail = ({ c }) => (
  <>
    <div style={{ fontSize: 20, fontWeight: 900, color: '#2D2D2D' }}>
      {pickLang(c.name, 'vi') || pickLang(c.name, 'en')}
    </div>
    {(c.description?.vi || c.description?.en) && (
      <Section icon="🧭" title="Mô tả">
        <Bilingual vi={c.description?.vi} en={c.description?.en} />
      </Section>
    )}
    {(c.how_to_solve?.vi || c.how_to_solve?.en) && (
      <Section icon="💡" title="Cách xử lý">
        <Bilingual vi={c.how_to_solve?.vi} en={c.how_to_solve?.en} />
      </Section>
    )}
    {(c.explanation?.vi || c.explanation?.en) && (
      <Section icon="📝" title="Giải thích">
        <Bilingual vi={c.explanation?.vi} en={c.explanation?.en} />
      </Section>
    )}
    {c.example_quiz && (
      <Section icon="🎯" title="Ví dụ">
        <span dangerouslySetInnerHTML={{ __html: c.example_quiz }} />
      </Section>
    )}
  </>
);

const TagDetailRender = ({ tag }) => {
  if (!tag) {
    return (
      <div style={{
        padding: '20px 16px', textAlign: 'center', color: '#9A9A9A',
        fontSize: 13, fontStyle: 'italic',
      }}>
        Chọn 1 từ / cấu trúc bên trên để xem chi tiết.
      </div>
    );
  }
  const c = tag.content || {};
  if (tag.tag_type_code === 'vocabulary' || tag.tag_type_code === 'vocabulary_topic') return <VocabDetail tag={tag} />;
  if (tag.tag_type_code === 'grammar' || tag.tag_type_code === 'grammar_topic')       return <GrammarDetail c={c} />;
  if (tag.tag_type_code === 'hint' || tag.tag_type_code === 'trap')                   return <HintTrapDetail c={c} />;
  return <pre style={{ fontSize: 12 }}>{JSON.stringify(c, null, 2)}</pre>;
};

// Pick the tag that matches the correct answer (so the right pane opens with
// useful context by default). Prefers a vocabulary tag whose name === the
// correct answer; falls back to a tag whose code contains the answer; else null.
const pickDefaultTag = (question) => {
  const tags = (question?.tag_details || []).filter((t) => TYPE_META[t.tag_type_code]);
  if (!tags.length) return null;
  const ans = question?.accepted_answers?.[0];
  if (!ans) return tags[0];
  const target = String(ans).trim().toLowerCase();
  if (!target) return tags[0];

  const tagName = (t) =>
    (typeof t.name === 'object' ? (t.name?.en || t.name?.vi || '') : (t.name || ''))
      .toString().trim().toLowerCase();

  // 1) Vocabulary tag whose name matches the correct answer exactly
  const exactVocab = tags.find(
    (t) => t.tag_type_code === 'vocabulary' && tagName(t) === target,
  );
  if (exactVocab) return exactVocab;
  // 2) Any tag whose name matches exactly
  const exactAny = tags.find((t) => tagName(t) === target);
  if (exactAny) return exactAny;
  // 3) Vocabulary tag whose code contains the answer (e.g. VOC_TRAFFIC_01)
  const codeContainsVocab = tags.find(
    (t) => t.tag_type_code === 'vocabulary' && (t.code || '').toLowerCase().includes(target),
  );
  if (codeContainsVocab) return codeContainsVocab;
  // 4) First vocabulary tag, else first tag
  return tags.find((t) => t.tag_type_code === 'vocabulary') || tags[0];
};

const QuestionLearnPopup = ({ question, onClose }) => {
  const [selectedTag, setSelectedTag] = useState(() => pickDefaultTag(question));
  const [showEn, setShowEn] = useState(false);
  const detailRef = useRef(null);

  // Re-default the right pane when the popup is reused for a different question.
  useEffect(() => {
    setSelectedTag(pickDefaultTag(question));
  }, [question?.id]);

  // Click-pill handler that also smooth-scrolls to detail pane on mobile
  // (where panes stack vertically and the detail is below the explanations).
  const selectTag = (tag) => {
    setSelectedTag(tag);
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      requestAnimationFrame(() => {
        detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const tags = (question?.tag_details || []).filter((t) => TYPE_META[t.tag_type_code]);
  const tagsByCode = React.useMemo(
    () => Object.fromEntries((question?.tag_details || []).map((t) => [t.code, t])),
    [question]
  );
  const explainVi = question?.teacher_note?.explain_vn;
  const explainEn = question?.teacher_note?.explain_en;
  const explainGrammarVi = question?.teacher_note?.explain_grammar_vn;
  const explainGrammarEn = question?.teacher_note?.explain_grammar_en;
  const explainHintVi = question?.teacher_note?.explain_hint_vn;
  const explainHintEn = question?.teacher_note?.explain_hint_en;
  const isCorrect = question?.is_correct === true || question?.is_correct === 'true';

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF8E7', width: '100%', maxWidth: 960,
          maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column',
          borderRadius: 20, boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          fontFamily: '"Nunito", "Baloo 2", sans-serif', overflow: 'hidden',
          animation: 'qlp-pop 0.18s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', borderBottom: '1px solid #E8E0CC', background: '#FFF8E7',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 999,
            background: isCorrect ? '#88C057' : '#E76F51',
            color: '#FFF', fontSize: 14, fontWeight: 900,
          }}>
            {isCorrect ? '✓' : '✗'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Câu {question?.order_index ?? '?'}
            </div>
            {(() => {
              const cleaned = (question?.question_text || '')
                .replace(/\(Example\)/gi, '')
                .replace(/\{\{\s*\d+\s*\}\}/g, '')
                .trim();
              if (!cleaned) return null;
              return (
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2D2D2D', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {cleaned}
                </div>
              );
            })()}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Đóng"
            style={{
              background: '#FFF', border: '1px solid #E8E0CC', borderRadius: '50%',
              width: 32, height: 32, fontSize: 20, fontWeight: 800, color: '#6B6B6B',
              cursor: 'pointer', padding: 0, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
        </div>

        {/* Body — split into LEFT (summary/explain/chips) and RIGHT (sticky detail).
            Stacks vertically on mobile (< 768px), side-by-side on desktop. */}
        <div className="qlp-body">
          <div className="qlp-left">
          {/* Answer summary */}
          {(question?.accepted_answers?.[0] || question?.user_answer?.answer) && (
            <div style={{
              background: '#FFF', border: '1px solid #E8E0CC', borderRadius: 12,
              padding: '8px 12px', marginBottom: 12, fontSize: 13,
              display: 'flex', flexWrap: 'wrap', gap: 12,
            }}>
              {question?.user_answer?.answer && !isCorrect && (
                <span>
                  <span style={{ color: '#9A9A9A', marginRight: 4 }}>Bạn trả lời:</span>
                  <span style={{ color: '#E76F51', textDecoration: 'line-through', fontWeight: 700 }}>
                    {question.user_answer.answer}
                  </span>
                </span>
              )}
              {question?.accepted_answers?.[0] && (
                <span>
                  <span style={{ color: '#9A9A9A', marginRight: 4 }}>Đáp án đúng:</span>
                  <span style={{ color: '#5E9E3E', fontWeight: 800 }}>{question.accepted_answers[0]}</span>
                </span>
              )}
            </div>
          )}

          {/* Manh mối — new_script from teacher_note */}
          {question?.teacher_note?.new_script && (
            <div style={{
              background: '#FFF', border: '1px solid #E8E0CC', borderRadius: 12,
              padding: '10px 12px', marginBottom: 12, borderLeft: '4px solid #F4A261',
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#D97706',
                textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <span>🔍</span> Manh Mối
              </div>
              <div style={{ fontSize: 15, color: '#2D2D2D', lineHeight: 1.6, fontWeight: 600, fontStyle: 'italic' }}>
                {question.teacher_note.new_script}
              </div>
            </div>
          )}

          {/* Explanations (general + grammar + hint). Single EN/VI toggle applied to all. */}
          {(explainVi || explainEn || explainGrammarVi || explainGrammarEn || explainHintVi || explainHintEn) && (
            <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Header with toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>💡</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#6B4F2B', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Giải thích
                </span>
                {((explainVi && explainEn) || (explainGrammarVi && explainGrammarEn) || (explainHintVi && explainHintEn)) && (
                  <button
                    type="button"
                    onClick={() => setShowEn((v) => !v)}
                    style={{
                      marginLeft: 'auto', background: 'transparent',
                      border: '1px solid #E8E0CC', borderRadius: 8, padding: '2px 8px',
                      fontSize: 11, fontWeight: 700, color: '#6B6B6B', cursor: 'pointer',
                    }}
                  >
                    {showEn ? 'VI' : 'EN'}
                  </button>
                )}
              </div>

              {/* General explanation */}
              {(explainVi || explainEn) && (
                <div style={{
                  background: '#FFF', border: '1px solid #E8E0CC', borderRadius: 12,
                  padding: '10px 12px', borderLeft: `4px solid ${TYPE_META.vocabulary.color}`,
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: TYPE_META.vocabulary.color,
                    textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <span>📘</span> Từ Vựng
                  </div>
                  <div style={{ fontSize: 14, color: '#2D2D2D', lineHeight: 1.6 }}>
                    <RenderAnnotated
                      text={showEn ? (explainEn || explainVi) : (explainVi || explainEn)}
                      tagsByCode={tagsByCode}
                      onSelectTag={selectTag}
                    />
                  </div>
                </div>
              )}

              {/* Grammar explanation */}
              {(explainGrammarVi || explainGrammarEn) && (
                <div style={{
                  background: '#FFF', border: '1px solid #E8E0CC', borderRadius: 12,
                  padding: '10px 12px', borderLeft: `4px solid ${TYPE_META.grammar.color}`,
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: TYPE_META.grammar.color,
                    textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <span>✏️</span> Ngữ pháp
                  </div>
                  <div style={{ fontSize: 14, color: '#2D2D2D', lineHeight: 1.6 }}>
                    <RenderAnnotated
                      text={showEn ? (explainGrammarEn || explainGrammarVi) : (explainGrammarVi || explainGrammarEn)}
                      tagsByCode={tagsByCode}
                      onSelectTag={selectTag}
                    />
                  </div>
                </div>
              )}

              {/* Hint & trap explanation */}
              {(explainHintVi || explainHintEn) && (
                <div style={{
                  background: '#FFF', border: '1px solid #E8D5EA', borderRadius: 12,
                  padding: '10px 12px', borderLeft: '4px solid #5B2E5E',
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: '#5B2E5E',
                    textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <span>🎯</span> Mẹo &amp; Bẫy
                  </div>
                  <div style={{ fontSize: 14, color: '#2D2D2D', lineHeight: 1.6 }}>
                    <RenderAnnotated
                      text={showEn ? (explainHintEn || explainHintVi) : (explainHintVi || explainHintEn)}
                      tagsByCode={tagsByCode}
                      onSelectTag={selectTag}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          </div>
          {/* RIGHT — sticky detail pane (always visible when tags exist). */}
          {tags.length > 0 && (
            <div className="qlp-right" ref={detailRef}>
              {selectedTag ? (
                <TagDetailRender tag={selectedTag} />
              ) : (
                <div style={{
                  padding: 24, textAlign: 'center', color: '#9A9A9A',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  minHeight: 200, justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 36 }}>📚</span>
                  <div style={{ fontSize: 13, fontStyle: 'italic', maxWidth: 240, lineHeight: 1.5 }}>
                    Bấm vào 1 từ vựng / cấu trúc trong giải thích bên trái để xem chi tiết.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <style>{`
          @keyframes qlp-pop {
            from { transform: scale(0.95); opacity: 0; }
            to   { transform: scale(1);    opacity: 1; }
          }
          .qlp-body {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            overflow: hidden;
          }
          .qlp-left {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            padding: 14px 16px 20px;
            -webkit-overflow-scrolling: touch;
          }
          .qlp-right {
            flex-shrink: 0;
            min-height: 280px;
            padding: 14px 16px 20px;
            background: #FFFCEF;
            border-top: 1px solid #E8E0CC;
            -webkit-overflow-scrolling: touch;
          }
          @media (min-width: 768px) {
            .qlp-body { flex-direction: row; }
            .qlp-left {
              flex: 0 0 60%;
              max-height: 100%;
              border-right: 1px solid #E8E0CC;
            }
            .qlp-right {
              flex: 1;
              min-height: 0;
              max-height: 100%;
              overflow-y: auto;
              border-top: none;
            }
          }
        `}</style>
      </div>
    </div>,
    document.body
  );
};

export default QuestionLearnPopup;
